// İlk Giriş / Aktivasyon — plan §4.1.3 + design ref #screen-first-login.
// /first-login?token=... (veya /activate/:token — query param kullanıyoruz).
// Başarılı → BE otomatik login (cookie + CSRF), auth-store güncellenir, dashboard'a yönlendir.

import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "../components/auth/AuthShell";
import { PasswordField } from "../components/auth/PasswordField";
import { evaluatePassword, rulesAllPass } from "../lib/password-strength";
import { firstLogin } from "../api/auth";
import { setCsrfToken } from "../api/client";
import { ApiError } from "../api/client";
import { rateLimitMessage } from "../lib/auth-rate-limit";
import { useAuthStore } from "../stores/auth-store";
import { useActivationGate } from "../hooks/use-activation-gate";
import { ACTIVATION_LINK_COPY } from "../lib/activation-link-ui";
import {
  ActivationLinkChecking,
  ActivationLinkNotice,
} from "../components/auth/ActivationLinkNotice";

export function FirstLogin(): JSX.Element {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const setAuthenticated = useAuthStore((s) => s.setAuthenticated);
  const token = params.get("token") ?? "";
  // Davet maili linkindeki kind=client ipucu (project-participants.ts) — token
  // doğrulanmadan userKind bilinemediği için copy'yi bu param seçer. Yetki VERMEZ.
  const isClientInvite = params.get("kind") === "client";
  const pitch = isClientInvite
    ? "Danışmanınız sizi bir projenin form yanıtlarını doldurmanız için davet etti."
    : "Hoş geldiniz. Birkaç adım, ardından raporlamaya başlayalım.";
  const activateSubtitle = isClientInvite
    ? "Hesabınızı aktive etmek için bir şifre oluşturun; ardından form yanıtlarına erişeceksiniz."
    : "Hesabınızı aktive etmek için yeni bir şifre oluşturun. Sonraki girişlerde bunu kullanacaksınız.";

  // Açılışta bağlantı ön kontrolü (2026-10-20): kullanılmış/süresi dolmuş bağlantıda form hiç
  // gösterilmez; uç hata verirse form (fail-open). Token yokken çağrı yapılmaz.
  const gate = useActivationGate(token);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const strength = useMemo(() => evaluatePassword(password), [password]);
  const mismatch = confirm.length > 0 && password !== confirm;
  const rulesOk = rulesAllPass(strength.rules);
  const canSubmit = !loading && rulesOk && !mismatch && password.length >= 12 && token.length > 0;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await firstLogin(token, password);
      setCsrfToken(res.csrfToken);
      setAuthenticated(res.user);
      void navigate("/", { replace: true });
    } catch (err) {
      const limited = rateLimitMessage(err);
      if (limited) {
        setError(limited);
      } else if (err instanceof ApiError) {
        if (err.code === "token_invalid" || err.code === "token_expired") {
          // Token doğrulanmadan userKind bilinmiyor (client/internal ayrımı yapılamaz,
          // design-ref "First-login müşteri dili") — nötr metin her iki kitleye de çalışır.
          setError(
            "Aktivasyon linki geçersiz veya süresi dolmuş. Yöneticinizle veya danışmanınızla iletişime geçin.",
          );
        } else if (err.status === 422) {
          setError(err.message || "Şifre gereksinimleri karşılanmıyor.");
        } else {
          setError(err.message);
        }
      } else {
        setError(err instanceof Error ? err.message : "Beklenmedik bir hata oluştu.");
      }
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <AuthShell pitch={pitch}>
        <h2
          className="text-2xl mb-2"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-error)" }}
        >
          Aktivasyon linki eksik
        </h2>
        <p className="mb-6" style={{ color: "var(--color-text-2)" }}>
          Lütfen yöneticinizden veya danışmanınızdan gelen davet e-postasındaki linki kullanın.
        </p>
        <Link
          to="/"
          className="inline-block px-4 py-2.5 rounded-lg font-medium"
          style={{
            background: "transparent",
            border: "1px solid var(--color-border)",
            color: "var(--color-text)",
          }}
        >
          Giriş ekranına dön
        </Link>
      </AuthShell>
    );
  }

  if (gate.mode === "checking") {
    return (
      <AuthShell pitch={pitch}>
        <ActivationLinkChecking text={ACTIVATION_LINK_COPY.checking} />
      </AuthShell>
    );
  }

  if (gate.mode === "blocked") {
    return (
      <AuthShell pitch={pitch}>
        <ActivationLinkNotice status={gate.status} kind="first_login" />
      </AuthShell>
    );
  }

  return (
    <AuthShell pitch={pitch}>
      <span
        className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold mb-4"
        style={{
          background: "color-mix(in srgb, var(--color-warning) 15%, white)",
          color: "var(--color-accent)",
          border: "1px solid color-mix(in srgb, var(--color-warning) 35%, white)",
        }}
      >
        İlk Giriş
      </span>
      <h2
        className="text-3xl mb-2"
        style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
      >
        Şifrenizi belirleyin
      </h2>
      <p className="mb-6" style={{ color: "var(--color-text-2)" }}>
        {activateSubtitle}
      </p>

      <form onSubmit={(e) => void onSubmit(e)}>
        <PasswordField
          label="Yeni Şifre"
          value={password}
          onChange={setPassword}
          disabled={loading}
          autoFocus
          strength={strength}
          showStrength
          showRules
        />
        <PasswordField
          label="Şifreyi Tekrarla"
          value={confirm}
          onChange={setConfirm}
          disabled={loading}
        />
        {mismatch ? (
          <p className="mb-3 text-xs" role="alert" style={{ color: "var(--color-error)" }}>
            Şifreler eşleşmiyor.
          </p>
        ) : null}
        {error ? (
          <div
            role="alert"
            data-alert-variant="error"
            className="mb-4 px-3 py-2 rounded-lg text-sm"
            style={{
              background: "color-mix(in srgb, var(--color-error) 10%, white)",
              color: "var(--color-error)",
              border: "1px solid color-mix(in srgb, var(--color-error) 35%, white)",
            }}
          >
            {error}
          </div>
        ) : null}
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full px-4 py-3 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-surface)",
            fontSize: "0.95rem",
          }}
        >
          {loading ? "Hesap aktive ediliyor…" : "Hesabı Aktive Et"}
        </button>
      </form>
    </AuthShell>
  );
}
